import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import camelcaseKeys from 'camelcase-keys';

import { findCheapestTicket } from '../../utils/tickets';
import {
  SeriesEventWidget,
  SeriesTicketType,
} from '../event-details/event-detail-types';
import {
  Answer,
  QuestionTypeWithBankId,
} from '../registration-form/custom-form-factory-types';
import {
  loadSeriesDefaultWidget,
  loadWidgetQuestionsThunk,
  loadWidgetTicketsThunk,
} from './series-thunk';

export interface SeriesSliceState {
  activeSeries: string | null;
  eventSeries: Record<string, string>;
  eventsWidgets: {
    data: Array<SeriesEventWidget & { tickets: SeriesTicketType[] }>;
    loading: boolean;
    error: any;
  };
  tickets: {
    data: Record<string, SeriesTicketType[]>;
    loading: boolean;
    error: any;
  };
  selectedEvents: {
    [eventId: string]: {
      isSelected: boolean;
    };
  };
  selectedTicketIds: Record<string, string>;
  questions: {
    data: Record<string, QuestionTypeWithBankId[]>;
    loading: boolean;
    error: string | null;
    answers: Record<string, Answer[]>;
    userDetails: Record<string, string>;
  };
  erroredEventIds: Record<
    string,
    { isErrored: boolean; errorMessages?: string[] }
  >;
}

export const initialState: SeriesSliceState = {
  activeSeries: null,
  eventSeries: {},
  eventsWidgets: {
    data: [],
    loading: false,
    error: null,
  },
  tickets: {
    data: {},
    loading: false,
    error: null,
  },
  selectedEvents: {},
  selectedTicketIds: {},
  questions: {
    data: {},
    loading: false,
    error: null,
    answers: {},
    userDetails: {},
  },
  erroredEventIds: {},
};

const seriesSlice = createSlice({
  name: 'series',
  initialState,
  reducers: {
    toggleEventSelection: (
      state,
      action: PayloadAction<{ eventId: string }>,
    ) => {
      const { eventId } = action.payload;
      const widgetEvent = state.eventsWidgets.data.find(
        widget => widget.event.externalId === eventId,
      )?.event;

      if (!widgetEvent || !widgetEvent.published) {
        return;
      }

      // If the event is not selected, we need to select the best ticket
      if (!state.selectedEvents[eventId]?.isSelected) {
        // If for the event ticket is already selected, then only select the event
        if (state.selectedTicketIds[eventId]) {
          state.selectedEvents[eventId] = {
            isSelected: true,
          };
        } else {
          // If the event ticket is not selected then select the cheapest ticket
          const tickets =
            state.eventsWidgets.data.find(
              widget => widget.event.externalId === eventId,
            )?.tickets || [];
          const bestTicket = findCheapestTicket(tickets);
          if (bestTicket) {
            state.selectedEvents[eventId] = {
              isSelected: true,
            };
            state.selectedTicketIds[eventId] = bestTicket.externalId;
          }
        }
      } else if (state.selectedEvents[eventId]?.isSelected) {
        state.selectedEvents[eventId] = {
          isSelected: false,
        };
      }
    },
    selectAllEvents: state => {
      state.eventsWidgets.data.forEach(widget => {
        const tickets = widget.tickets || [];
        const widgetEvent = widget.event;

        if (!widgetEvent.published) {
          return;
        }

        if (state.erroredEventIds[widgetEvent.externalId]) {
          return;
        }

        const bestTicket = findCheapestTicket(tickets);
        if (bestTicket) {
          state.selectedEvents[widgetEvent.externalId] = {
            isSelected: true,
          };
          state.selectedTicketIds[widgetEvent.externalId] =
            bestTicket.externalId;
        }
      });
    },
    selectEvent: (state, action: PayloadAction<{ eventId: string }>) => {
      const { eventId } = action.payload;
      const widgetEvent = state.eventsWidgets.data.find(
        widget => widget.event.externalId === eventId,
      )?.event;

      if (!widgetEvent || !widgetEvent.published) {
        return;
      }

      if (state.erroredEventIds[widgetEvent.externalId]) {
        return;
      }

      state.selectedEvents[eventId] = {
        isSelected: true,
      };
    },
    unselectEvent: (state, action: PayloadAction<{ eventId: string }>) => {
      const { eventId } = action.payload;
      state.selectedEvents[eventId] = {
        isSelected: false,
      };
    },
    unselectAllEvents: state => {
      Object.keys(state.selectedEvents).forEach(eventId => {
        state.selectedEvents[eventId] = {
          isSelected: false,
        };
      });
    },
    addTicket(
      state: SeriesSliceState,
      { payload }: { payload: { eventId: string; ticketId: string } },
    ) {
      state.selectedTicketIds[payload.eventId] = payload.ticketId;
    },
    updateAnswers: (
      state,
      action: PayloadAction<{
        userDetails: Record<string, string>;
        answers?: Record<string, Answer[]>;
      }>,
    ) => {
      state.questions.userDetails = action.payload.userDetails;

      if (action.payload.answers) {
        state.questions.answers = action.payload.answers;
      }
    },
    updateErroredEventIds: (
      state,
      action: PayloadAction<{
        eventId: string;
        value?: boolean;
        errorMessages?: string[];
      }>,
    ) => {
      state.erroredEventIds[action.payload.eventId] = {
        isErrored: action.payload.value ?? true,
        errorMessages: action.payload.errorMessages,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadSeriesDefaultWidget.pending, state => {
        state.eventsWidgets.loading = true;
        state.eventsWidgets.error = null;
      })
      .addCase(loadSeriesDefaultWidget.fulfilled, (state, action) => {
        state.activeSeries = action.payload.active_series;
        state.eventSeries = action.payload.event_series;

        const widgets = action.payload.active_series_events_widgets;

        const nextData = [];
        for (const key in widgets) {
          const widget = widgets[key];

          nextData.push({
            event: camelcaseKeys(widget.event),
            images: camelcaseKeys(widget.images),
            settings: camelcaseKeys(widget.settings),
            tickets: [],
          });
        }

        state.eventsWidgets.data = nextData;
        state.eventsWidgets.loading = false;
      })
      .addCase(loadSeriesDefaultWidget.rejected, (state, action) => {
        state.eventsWidgets.error = action.payload;
        state.eventsWidgets.loading = false;
      })
      .addCase(loadWidgetTicketsThunk.pending, state => {
        state.tickets.loading = true;
        state.tickets.error = null;
      })
      .addCase(loadWidgetTicketsThunk.fulfilled, (state, action) => {
        for (const eventExternalId in action.payload) {
          const tickets = action.payload[eventExternalId];
          state.tickets.data[eventExternalId] = tickets.map(ticket =>
            camelcaseKeys(ticket),
          );

          for (let i = 0; i < state.eventsWidgets.data.length; i++) {
            const widget = state.eventsWidgets.data[i];

            if (widget.event.externalId === eventExternalId) {
              state.eventsWidgets.data[i] = {
                ...state.eventsWidgets.data[i],
                tickets: tickets.map(t => camelcaseKeys(t)),
              };
              break;
            }
          }
        }

        state.tickets.loading = false;
      })
      .addCase(loadWidgetTicketsThunk.rejected, (state, action) => {
        state.tickets.error = action.payload;
        state.tickets.loading = false;
      })
      .addCase(loadWidgetQuestionsThunk.rejected, (state, action) => {
        state.questions.error = action.payload as string;
        state.questions.loading = false;
      })
      .addCase(loadWidgetQuestionsThunk.pending, state => {
        state.questions.loading = true;
        state.questions.error = null;
      })
      .addCase(loadWidgetQuestionsThunk.fulfilled, (state, action) => {
        state.questions.data = action.payload;
        state.questions.loading = false;
      });
  },
});

export const seriesReducer = seriesSlice.reducer;

export const {
  addTicket,
  toggleEventSelection,
  selectAllEvents,
  unselectAllEvents,
  selectEvent,
  unselectEvent,
  updateAnswers,
  updateErroredEventIds,
} = seriesSlice.actions;
